import React from 'react';
import { connect } from '../../../../redux/store';
import {
  ErrorMessage,
  EmptyMessage,
  LoadingMessage,
  C,
} from '@kineticdata/bundle-common';
import { I18n } from '@kineticdata/react';
import { DefaultActions } from './';
import { CONTENT_MANAGER_MODE } from '../helpers';
import { FormCard } from '../../FormCard';
import { ExternalOfferingCard } from '../../ExternalOfferingCard';
import { List } from 'immutable';

const mapStateToProps = (state, props) => {
  const isPublicMode = props.mode === CONTENT_MANAGER_MODE.PUBLIC;
  const isViewMode = props.mode !== CONTENT_MANAGER_MODE.EDIT;
  const id = props.activeItem && props.activeItem.id;
  const serviceId = props.values.get(C.FIELD_SERVICE_ID);
  const categorySlug = serviceId.toLowerCase();

  return {
    authenticated: state.app.authenticated,
    isPublicMode,
    isViewMode,
    id,
    offerings: List(state.app.kapp.forms || []).filter(form =>
      form.categorizations.some(c => c.category.slug === categorySlug),
    ),
    offeringsLoading: !state.app.catalog.init && state.app.catalog.loading,
    offeringsError: state.app.catalog.errors.first(),
    externalOfferings: state.externalOfferings.data.filter(offering =>
      offering.values['Service Id'].some(id => id === serviceId),
    ),
    externalOfferingsError: state.externalOfferings.error,
  };
};

const ServiceOfferingsRenderer = connect(mapStateToProps)(
  ({
    mode,
    isPublicMode,
    isViewMode,
    content,
    values,
    activeItem,
    handleValuesChangeDirect,
    id,
    offerings,
    offeringsLoading,
    offeringsError,
    externalOfferings,
    externalOfferingsError,
    ...props
  }) => {
    const allOfferings = offerings
      .concat(externalOfferings)
      .sortBy(o => o.name || (o.values && o.values['Name']));

    return !!activeItem ? (
      <>
        <h2>
          <I18n>{activeItem.title}</I18n>
        </h2>

        {!isViewMode && (
          <div className="form mb-3">
            <div className="form-group">
              <label htmlFor="title">
                <I18n>Section Title</I18n>
              </label>
              <input
                type="text"
                id="title"
                value={activeItem.title}
                onChange={props.handleContentChange(id, 'title')}
              />
            </div>

            <div className="alert alert-bar alert-info">
              <em>
                <I18n>
                  To manage internal service offerings for this service, a
                  service offering owner must add or remove the service offering
                  form to the category corresponding to this service.
                </I18n>
              </em>
            </div>
            <div className="alert alert-bar alert-info">
              <em>
                <I18n>
                  To manage external service offerings to this service, an SMO
                  member must add or remove the offerings in the External
                  Service Offerings admin form.
                </I18n>
              </em>
            </div>
          </div>
        )}

        {!props.authenticated ? (
          <ErrorMessage title="Please log in to view service offerings." />
        ) : offeringsError ? (
          <ErrorMessage
            title="The service offerings failed to load."
            message={offeringsError.message}
          />
        ) : offeringsLoading ? (
          <LoadingMessage />
        ) : allOfferings.size === 0 ? (
          <EmptyMessage title="There are no service offerings for this service." />
        ) : (
          <>
            {externalOfferingsError && (
              <div className="alert alert-danger alert-bar">
                <I18n>Some service offerings failed to load.</I18n>
                <br />
                <small className="text-muted">
                  {externalOfferingsError.message}
                </small>
              </div>
            )}
            <div className="cards">
              {allOfferings.map(
                offering =>
                  offering.slug ? (
                    <FormCard
                      key={offering.slug}
                      form={offering}
                      path={isViewMode ? `forms/${offering.slug}` : undefined}
                      hideFavorite={!isPublicMode}
                    />
                  ) : (
                    <ExternalOfferingCard
                      key={offering.id}
                      offering={offering}
                    />
                  ),
              )}
            </div>
          </>
        )}

        {!isViewMode && (
          <DefaultActions
            id={id}
            isDirty={activeItem.isDirty}
            handleResetSection={props.handleResetSection}
            handleContentDelete={props.handleContentDelete}
            actionsPending={props.actionsPending}
          />
        )}
      </>
    ) : null;
  },
);

// Metadata for this renderer
ServiceOfferingsRenderer.ID = 'SERVICE_OFFERINGS';
ServiceOfferingsRenderer.NAME = 'Service Offerings';
ServiceOfferingsRenderer.TYPES = ['Service'];

// Define fields managed by this section so we can know which fields to reset
// when the section is reset. Can store additional metadata for fields to use in
// the renderer above.
// ServiceOfferingsRenderer.FIELDS = {};

export default ServiceOfferingsRenderer;
